import { useAuth } from "AuthProvider";
import { RoutePaths } from "enums/RoutePaths";
import useFetch from "hooks/useFetch";
import {
  CAMPAIGN_KINDS,
  COLLECTION_MODES,
  CollectionMode,
  formatOre,
  formatKr,
  progressPercent,
} from "pages/CampaignPage/campaignKind";
import { formatOrgNumber } from "pages/AdminLayout/menuConfig";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  Card,
  Column,
  EmptyState,
  Icon,
  PageHeader,
  SegmentedControl,
  Skeleton,
  Table,
  TextField,
  styles as ui,
} from "stories/Admin";
import {
  CampaignSummary,
  CategoryRow,
  DateRange,
  DonationSummary,
  MonthSummary,
  MosqueSummary,
  RANGE_PRESETS,
  RangePreset,
  categoryRows,
  donationSummaryUrl,
  hasCategories,
  hasCategoryData,
  fillMonths,
  isRangeValid,
  longMonth,
  presetRange,
  providerName,
  providerSplit,
  rangeParams,
  shortMonth,
  sortByRaised,
  thisMonthOre,
} from "./donationSummary";
import styles from "./DonationSummaryPage.module.scss";

const nf = (n: number | null | undefined) =>
  (Number(n) || 0).toLocaleString("nb-NO");

const Stat: React.FC<{
  label: string;
  value: React.ReactNode;
  hint?: React.ReactNode;
}> = ({ label, value, hint }) => (
  <div className={styles.stat}>
    <span className={styles.statLabel}>{label}</span>
    <b className={styles.statValue}>{value}</b>
    {hint && <small className={styles.statHint}>{hint}</small>}
  </div>
);

/** Fremdriftslinje mot målet. */
const Progress: React.FC<{ percent: number; label?: string }> = ({
  percent,
  label,
}) => (
  <div
    className={styles.progress}
    role="progressbar"
    aria-valuemin={0}
    aria-valuemax={100}
    aria-valuenow={percent}
    aria-label={label}
  >
    <span style={{ width: `${percent}%` }} />
  </div>
);

/** Søylediagram per måned (ren CSS, ingen diagrambibliotek). */
const MonthChart: React.FC<{ months: MonthSummary[] }> = ({ months }) => {
  const max = Math.max(0, ...months.map((m) => m.raisedOre));
  return (
    <figure className={styles.chart}>
      <div className={styles.chartArea}>
        <span className={styles.chartMax}>
          {max > 0 ? formatOre(max) : "0 kr"}
        </span>
        <div className={styles.bars}>
          {months.map((m) => {
            const height = max > 0 ? (m.raisedOre / max) * 100 : 0;
            const text = `${longMonth(m.month)}: ${formatOre(m.raisedOre)}${
              m.chargeCount ? ` · ${nf(m.chargeCount)} gaver` : ""
            }`;
            return (
              <div
                key={m.month}
                className={styles.barCol}
                tabIndex={0}
                aria-label={text}
              >
                <span className={styles.tip} role="tooltip">
                  {text}
                </span>
                <span
                  className={styles.bar}
                  style={{
                    height: `${height}%`,
                    minHeight: m.raisedOre > 0 ? 3 : 0,
                  }}
                />
              </div>
            );
          })}
        </div>
      </div>
      <div className={styles.barLabels} aria-hidden="true">
        {months.map((m) => (
          <span key={m.month}>{shortMonth(m.month)}</span>
        ))}
      </div>
      <table className={ui.srOnly}>
        <caption>Innsamlet per måned</caption>
        <tbody>
          {months.map((m) => (
            <tr key={m.month}>
              <th scope="row">{longMonth(m.month)}</th>
              <td>{formatOre(m.raisedOre)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </figure>
  );
};

/** Kategoriene i én kampanje (utvidet rad i tabellen). */
const CategoryBreakdown: React.FC<{ rows: CategoryRow[]; name: string }> = ({
  rows,
  name,
}) => (
  <ul className={styles.breakdown} aria-label={`Kategorier i ${name}`}>
    {rows.map((r) => (
      <li key={r.label}>
        <span className={r.category ? styles.catName : styles.catNone}>
          {r.label}
        </span>
        <Progress percent={r.percent} label={`${r.label}: ${r.percent} %`} />
        <span className={styles.catAmount}>
          <b>{formatOre(r.raisedOre)}</b>
          <span className={styles.muted}> · {r.percent} %</span>
        </span>
        <span className={styles.catMeta}>
          {nf(r.donorCount)} givere
          {r.activeMonthlyCount > 0 && ` · ${nf(r.activeMonthlyCount)} faste`}
        </span>
      </li>
    ))}
  </ul>
);

const kindOf = (mode: unknown): CollectionMode | null =>
  typeof mode === "string" && (COLLECTION_MODES as string[]).includes(mode)
    ? (mode as CollectionMode)
    : null;

interface Props {
  /** «all» = superadmin: alle moskeer, med tabell per moské. */
  scope: "mosque" | "all";
}

/**
 * «Innsamlet»: gaver gitt i iBønn med Vipps og kort. Moské-admin ser sin
 * moské; superadmin ser alle moskeer.
 */
const DonationSummaryPage: React.FC<Props> = ({ scope }) => {
  const { selectedOrgNumber } = useAuth();
  const orgNr = scope === "mosque" ? selectedOrgNumber : null;
  const { execute, lastError } = useFetch<DonationSummary>();
  const executeRef = useRef(execute);
  executeRef.current = execute;
  const lastErrorRef = useRef(lastError);
  lastErrorRef.current = lastError;

  const [preset, setPreset] = useState<RangePreset>("all");
  const [custom, setCustom] = useState<DateRange>({});
  const [data, setData] = useState<DonationSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [failure, setFailure] = useState<"missing" | "error" | null>(null);
  /** Kampanjer med utvidet kategoriliste. */
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const toggleExpanded = (id: number) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const range = useMemo(
    () => presetRange(preset, new Date(), custom),
    [preset, custom],
  );
  const rangeOk = isRangeValid(range);

  const load = useCallback(async () => {
    if (scope === "mosque" && !orgNr) return;
    if (!rangeOk) return;
    setIsLoading(true);
    const result = await executeRef.current(
      donationSummaryUrl(orgNr),
      "GET",
      undefined,
      rangeParams(range),
    );
    if (result) {
      setData(result);
      setFailure(null);
    } else {
      const code = lastErrorRef.current()?.code;
      setFailure(code === 404 || code === 405 ? "missing" : "error");
    }
    setIsLoading(false);
  }, [scope, orgNr, range, rangeOk]);

  useEffect(() => {
    load();
  }, [load]);

  const now = new Date();
  const months = fillMonths(data?.byMonth, now);
  const split = providerSplit(data?.byProvider);
  const campaigns = sortByRaised(data?.byCampaign);
  const mosques = sortByRaised(data?.byMosque);

  const campaignColumns: Array<Column<CampaignSummary>> = [
    {
      key: "name",
      header: "Kampanje",
      render: (c) => {
        const kind = kindOf(c.collectionMode);
        return (
          <div className={styles.nameCell}>
            <b>{c.name}</b>
            <span className={styles.badges}>
              {kind && kind !== "IN_APP" && (
                <Badge tone={CAMPAIGN_KINDS[kind].tone}>
                  Nå: {CAMPAIGN_KINDS[kind].badge}
                </Badge>
              )}
              {c.activated === false && <Badge tone="grey">Inaktiv</Badge>}
            </span>
            {hasCategories(c.byCategory) && (
              <button
                type="button"
                className={styles.expandBtn}
                aria-expanded={expanded.has(c.campaignId)}
                onClick={() => toggleExpanded(c.campaignId)}
              >
                <Icon
                  name="chevronRight"
                  size={14}
                  strokeWidth={2.4}
                  className={
                    expanded.has(c.campaignId) ? styles.chevOpen : undefined
                  }
                />
                {expanded.has(c.campaignId)
                  ? "Skjul kategorier"
                  : `Kategorier (${categoryRows(c.byCategory).length})`}
              </button>
            )}
          </div>
        );
      },
    },
    {
      key: "raised",
      header: "Innsamlet",
      numeric: true,
      align: "right",
      render: (c) => <b>{formatOre(c.raisedOre)}</b>,
    },
    {
      key: "goal",
      header: "Mål",
      width: "30%",
      render: (c) => {
        if (!c.targetAmount || c.targetAmount <= 0) {
          return <span className={styles.muted}>Ingen mål</span>;
        }
        const percent = progressPercent(c.raisedOre, c.targetAmount);
        return (
          <div className={styles.goalCell}>
            <Progress
              percent={percent}
              label={`${c.name}: ${percent} % av målet`}
            />
            <span className={styles.muted}>
              {percent} % av {formatKr(c.targetAmount)}
            </span>
          </div>
        );
      },
    },
    {
      key: "donors",
      header: "Givere",
      numeric: true,
      align: "right",
      render: (c) => nf(c.donorCount),
    },
    {
      key: "monthly",
      header: "Faste månedlige",
      numeric: true,
      align: "right",
      render: (c) => nf(c.activeMonthlyCount),
    },
  ];

  const categoryColumns: Array<Column<CategoryRow>> = [
    {
      key: "name",
      header: "Kategori",
      render: (r) => (
        <b className={r.category ? undefined : styles.catNone}>{r.label}</b>
      ),
    },
    {
      key: "raised",
      header: "Innsamlet",
      numeric: true,
      align: "right",
      render: (r) => <b>{formatOre(r.raisedOre)}</b>,
    },
    {
      key: "share",
      header: "Andel",
      width: "28%",
      render: (r) => (
        <div className={styles.shareCell}>
          <Progress percent={r.percent} label={`${r.label}: ${r.percent} %`} />
          <span className={styles.muted}>{r.percent} %</span>
        </div>
      ),
    },
    {
      key: "donors",
      header: "Givere",
      numeric: true,
      align: "right",
      render: (r) => nf(r.donorCount),
    },
    {
      key: "monthly",
      header: "Faste månedlige",
      numeric: true,
      align: "right",
      render: (r) => nf(r.activeMonthlyCount),
    },
  ];

  const mosqueColumns: Array<Column<MosqueSummary>> = [
    {
      key: "name",
      header: "Moské",
      render: (m) => (
        <div className={styles.nameCell}>
          <b>{m.name || formatOrgNumber(m.orgNr)}</b>
          {m.name && (
            <span className={styles.muted}>{formatOrgNumber(m.orgNr)}</span>
          )}
        </div>
      ),
    },
    {
      key: "raised",
      header: "Innsamlet",
      numeric: true,
      align: "right",
      render: (m) => <b>{formatOre(m.raisedOre)}</b>,
    },
    {
      key: "donors",
      header: "Givere",
      numeric: true,
      align: "right",
      render: (m) => nf(m.donorCount),
    },
    {
      key: "monthly",
      header: "Faste månedlige",
      numeric: true,
      align: "right",
      render: (m) => nf(m.activeMonthlyCount),
    },
  ];

  const filters = (
    <div className={styles.filters}>
      <SegmentedControl
        label="Periode"
        options={RANGE_PRESETS}
        value={preset}
        onChange={setPreset}
      />
      {preset === "custom" && (
        <div className={styles.dates}>
          <TextField
            label="Fra"
            type="date"
            value={custom.from ?? ""}
            max={custom.to || undefined}
            onChange={(e) => setCustom({ ...custom, from: e.target.value })}
          />
          <TextField
            label="Til"
            type="date"
            value={custom.to ?? ""}
            min={custom.from || undefined}
            onChange={(e) => setCustom({ ...custom, to: e.target.value })}
            error={rangeOk ? undefined : "Til-datoen må være etter fra-datoen."}
          />
        </div>
      )}
    </div>
  );

  const note = (
    <Alert tone="info" className={styles.note}>
      Gaver gitt via lenke eller direkte til moskeen vises ikke her. Fasiten er
      alltid Vipps-/Stripe-portalen.
    </Alert>
  );

  const header = (
    <PageHeader
      title={scope === "all" ? "Innsamlet i alle moskeer" : "Innsamlet"}
      subtitle={
        scope === "all"
          ? "Gaver gitt i iBønn med Vipps og kort, for alle moskeer."
          : "Gaver gitt i iBønn med Vipps og kort til kampanjene deres."
      }
      actions={
        <Button
          variant="secondary"
          icon={<Icon name="swap" size={16} />}
          onClick={load}
          loading={isLoading && !!data}
        >
          Oppdater
        </Button>
      }
    />
  );

  if (failure === "missing" && !data) {
    return (
      <>
        {header}
        <Card>
          <EmptyState
            icon="chart"
            title="Oversikten er ikke klar ennå"
            action={
              <Button variant="secondary" onClick={load}>
                Prøv igjen
              </Button>
            }
          >
            Serveren har ikke fått innsamlingsoversikten ennå. Prøv igjen
            senere.
          </EmptyState>
        </Card>
      </>
    );
  }

  const loadingFirst = isLoading && !data;

  return (
    <>
      {header}
      <div className={styles.page}>
        {filters}
        {note}
        {failure === "error" && (
          <Alert tone="error">
            Kunne ikke hente oversikten. Prøv igjen litt senere.
          </Alert>
        )}

        <div className={styles.kpis}>
          {loadingFirst ? (
            [0, 1, 2, 3].map((i) => (
              <Skeleton key={i} height={104} radius={18} />
            ))
          ) : (
            <>
              <Stat
                label="Totalt innsamlet"
                value={formatOre(data?.totalOre)}
                hint={
                  data?.paidChargeCount != null
                    ? `${nf(data.paidChargeCount)} betalinger`
                    : undefined
                }
              />
              <Stat
                label="Denne måneden"
                value={formatOre(thisMonthOre(data?.byMonth, now))}
                hint={longMonth(months[months.length - 1]?.month ?? "")}
              />
              <Stat
                label="Faste månedlige gaver"
                value={nf(data?.activeMonthlyCount)}
                hint={`${formatOre(data?.activeMonthlyOre)} per måned`}
              />
              <Stat label="Givere" value={nf(data?.donorCount)} />
            </>
          )}
        </div>

        <div className={styles.grid}>
          <Card
            title="Siste 12 måneder"
            subtitle="Innsamlet per måned, etter dato pengene ble trukket."
          >
            {loadingFirst ? (
              <Skeleton height={210} radius={14} />
            ) : (
              <MonthChart months={months} />
            )}
          </Card>

          <Card title="Vipps og kort">
            {loadingFirst ? (
              <Skeleton height={120} radius={14} />
            ) : (
              <div className={styles.split}>
                {split.map((p) => (
                  <div key={p.provider} className={styles.splitRow}>
                    <div className={styles.splitHead}>
                      <b>{providerName(p.provider)}</b>
                      <span className={ui.num}>
                        {formatOre(p.raisedOre)}
                        <span className={styles.muted}> · {p.percent} %</span>
                      </span>
                    </div>
                    <Progress
                      percent={p.percent}
                      label={`${providerName(p.provider)}: ${p.percent} %`}
                    />
                  </div>
                ))}
                {data &&
                  (data.oneTimeOre != null || data.monthlyOre != null) && (
                    <dl className={styles.dl}>
                      <dt>Engangsgaver</dt>
                      <dd className={ui.num}>{formatOre(data.oneTimeOre)}</dd>
                      <dt>Faste månedlige</dt>
                      <dd className={ui.num}>{formatOre(data.monthlyOre)}</dd>
                    </dl>
                  )}
              </div>
            )}
          </Card>
        </div>

        {!loadingFirst && hasCategoryData(data) && (
          <Card
            title="Per kategori"
            subtitle="Kategorien giveren valgte før betaling. Faste månedlige gaver teller i kategorien de ble startet med."
          >
            <Table
              columns={categoryColumns}
              rows={categoryRows(data?.byCategory)}
              rowKey={(r) => r.label}
              caption="Innsamlet per kategori"
            />
          </Card>
        )}

        {scope === "all" && (
          <Card title="Per moské">
            {loadingFirst ? (
              <Skeleton height={160} radius={14} />
            ) : (
              <Table
                columns={mosqueColumns}
                rows={mosques}
                rowKey={(m) => m.orgNr}
                caption="Innsamlet per moské"
                empty={
                  <EmptyState icon="mosque" title="Ingen gaver ennå">
                    Moskeer som har fått gaver i appen vises her.
                  </EmptyState>
                }
              />
            )}
          </Card>
        )}

        <Card
          title="Per kampanje"
          actions={
            scope === "mosque" ? (
              <Link className={styles.cardLink} to={RoutePaths.Campaign}>
                Til kampanjene
              </Link>
            ) : undefined
          }
        >
          {loadingFirst ? (
            <Skeleton height={160} radius={14} />
          ) : (
            <Table
              columns={campaignColumns}
              renderExpanded={(c) =>
                expanded.has(c.campaignId) && hasCategories(c.byCategory) ? (
                  <CategoryBreakdown
                    rows={categoryRows(c.byCategory)}
                    name={c.name}
                  />
                ) : null
              }
              rows={campaigns}
              rowKey={(c) => c.campaignId}
              caption="Innsamlet per kampanje"
              empty={
                <EmptyState icon="heart" title="Ingen gaver ennå">
                  Kampanjer med «Gaver i appen» vises her når noen har gitt.
                </EmptyState>
              }
            />
          )}
        </Card>
      </div>
    </>
  );
};

export default DonationSummaryPage;
